import axios from 'axios';
import { StoreFormData, ApiResponse, PaginatedResponse } from '../types/store.types';

// Configuração base da API
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/stores`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para tratar respostas de erro
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expirado ou inválido
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const storeService = {
  // Criar nova loja
  async createStore(storeData: StoreFormData): Promise<ApiResponse<StoreFormData>> {
    try {
      const formData = new FormData();
      
      // Adicionar dados básicos
      Object.keys(storeData).forEach((key) => {
        const value = storeData[key as keyof StoreFormData];
        
        if (key === 'products' || key === 'paymentMethods' || key === 'customFields') {
          // Arrays são enviados como JSON
          formData.append(key, JSON.stringify(value));
        } else if (key === 'customTheme' || key === 'businessHours' || key === 'socialMedia' || key === 'seo') {
          // Objetos são enviados como JSON
          formData.append(key, JSON.stringify(value));
        } else if (value instanceof File) {
          // Arquivos são enviados diretamente
          formData.append(key, value);
        } else if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
          // Arrays de arquivos
          value.forEach((file, index) => {
            formData.append(`${key}[${index}]`, file as File);
          });
        } else if (value !== null && value !== undefined && !Array.isArray(value) && typeof value !== 'object') {
          // Outros valores como string (não objetos)
          formData.append(key, String(value));
        }
      });

      const response = await api.post('/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao criar loja');
    }
  },

  // Buscar loja por ID
  async getStoreById(storeId: string): Promise<ApiResponse<StoreFormData>> {
    try {
      const response = await api.get(`/${storeId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar loja');
    }
  },

  // Buscar loja por slug
  async getStoreBySlug(slug: string): Promise<ApiResponse<StoreFormData>> {
    try {
      const response = await api.get(`/slug/${slug}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar loja');
    }
  },

  // Listar lojas do usuário
  async getUserStores(page = 1, limit = 10): Promise<PaginatedResponse<StoreFormData>> {
    try {
      const response = await api.get(`/user?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao listar lojas');
    }
  },

  // Atualizar loja
  async updateStore(storeId: string, storeData: Partial<StoreFormData>): Promise<ApiResponse<StoreFormData>> {
    try {
      const formData = new FormData();
      
      // Adicionar apenas os campos que foram alterados
      Object.keys(storeData).forEach((key) => {
        const value = storeData[key as keyof StoreFormData];
        
        if (key === 'products' || key === 'paymentMethods' || key === 'customFields') {
          formData.append(key, JSON.stringify(value));
        } else if (key === 'customTheme' || key === 'businessHours' || key === 'socialMedia' || key === 'seo') {
          formData.append(key, JSON.stringify(value));
        } else if (value instanceof File) {
          formData.append(key, value);
        } else if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
          value.forEach((file, index) => {
            formData.append(`${key}[${index}]`, file as File);
          });
        } else if (value !== null && value !== undefined && !Array.isArray(value) && typeof value !== 'object') {
          formData.append(key, String(value));
        }
      });

      const response = await api.put(`/${storeId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar loja');
    }
  },

  // Deletar loja
  async deleteStore(storeId: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/${storeId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao deletar loja');
    }
  },

  // Verificar disponibilidade do slug
  async checkSlugAvailability(slug: string): Promise<ApiResponse<{ available: boolean }>> {
    try {
      const response = await api.get(`/check-slug/${slug}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao verificar slug');
    }
  },

  // Publicar loja
  async publishStore(storeId: string): Promise<ApiResponse<StoreFormData>> {
    try {
      const response = await api.post(`/${storeId}/publish`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao publicar loja');
    }
  },

  // Pausar loja
  async pauseStore(storeId: string): Promise<ApiResponse<StoreFormData>> {
    try {
      const response = await api.post(`/${storeId}/pause`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao pausar loja');
    }
  },

  // Duplicar loja
  async duplicateStore(storeId: string): Promise<ApiResponse<StoreFormData>> {
    try {
      const response = await api.post(`/${storeId}/duplicate`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao duplicar loja');
    }
  },

  // Exportar dados da loja
  async exportStoreData(storeId: string): Promise<Blob> {
    try {
      const response = await api.get(`/${storeId}/export`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao exportar dados');
    }
  },

  // Importar dados para loja
  async importStoreData(storeId: string, file: File): Promise<ApiResponse<void>> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/${storeId}/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao importar dados');
    }
  },

  // Buscar lojas públicas (para marketplace)
  async getPublicStores(
    page = 1,
    limit = 12,
    filters?: {
      category?: string;
      search?: string;
      sortBy?: 'name' | 'createdAt' | 'rating';
      sortOrder?: 'asc' | 'desc';
    }
  ): Promise<PaginatedResponse<StoreFormData>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...filters,
      });

      const response = await api.get(`/public?${params}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar lojas públicas');
    }
  },

  // Avaliar loja
  async rateStore(storeId: string, rating: number, comment?: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.post(`/${storeId}/rate`, {
        rating,
        comment,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao avaliar loja');
    }
  },

  // Seguir loja
  async followStore(storeId: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.post(`/${storeId}/follow`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao seguir loja');
    }
  },

  // Parar de seguir loja
  async unfollowStore(storeId: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(`/${storeId}/follow`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Erro ao parar de seguir loja');
    }
  },
};

export default storeService;
